import { Star, TrendingUp, Target, Zap } from 'lucide-react';
import { useCountUp } from '@/hooks/useCountUp';
import { useState, useEffect, memo } from 'react';
import { useIsMobile } from '@/hooks/use-mobile';

interface AnimatedStatProps {
  value: number;
  label: string;
  icon: React.ElementType;
  color: string;
  bgColor: string;
  glowColor: string;
  delay?: number;
  index: number;
}

function AnimatedStat({ value, label, icon: Icon, color, bgColor, glowColor, delay = 0, index }: AnimatedStatProps) {
  const count = useCountUp({ end: value, duration: 2000, delay, enabled: true });
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), index * 150);
    return () => clearTimeout(timer);
  }, [index]);

  return (
    <div 
      className={`
        flex justify-between items-center p-4 rounded-xl ${bgColor} 
        border ${color.replace('text-', 'border-')}/30
        transform transition-all duration-500 ease-out
        ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}
        hover:scale-[1.02] hover:shadow-lg group
      `}
      style={{ 
        transitionDelay: `${index * 100}ms`,
        boxShadow: isVisible ? `0 4px 20px ${glowColor}` : 'none'
      }}
    >
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${bgColor} group-hover:scale-110 transition-transform duration-300`}>
          <Icon className={`h-5 w-5 ${color} group-hover:animate-pulse`} />
        </div>
        <span className="text-sm font-medium text-foreground">{label}</span>
      </div>
      <div className="relative">
        <span className={`font-mono text-2xl font-bold ${color} tabular-nums relative z-10`}>
          {count.toLocaleString()}
        </span>
        {/* Glow effect behind number */}
        <div className={`absolute inset-0 ${color.replace('text-', 'bg-')}/20 blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300`} />
      </div>
    </div>
  );
}

interface AnimatedStatsCounterProps {
  totalWins: number;
  totalDraws: number;
  totalGoals: number;
}

function AnimatedStatsCounterComponent({ totalWins, totalDraws, totalGoals }: AnimatedStatsCounterProps) {
  return (
    <div className="space-y-3">
      <AnimatedStat 
        value={totalWins} 
        label="Total Wins" 
        icon={Star} 
        color="text-emerald-400" 
        bgColor="bg-emerald-500/10"
        glowColor="rgba(16, 185, 129, 0.15)"
        delay={0}
        index={0}
      />
      <AnimatedStat 
        value={totalDraws} 
        label="Total Draws" 
        icon={Target} 
        color="text-amber-400" 
        bgColor="bg-amber-500/10"
        glowColor="rgba(245, 158, 11, 0.15)"
        delay={200}
        index={1}
      />
      <AnimatedStat 
        value={totalGoals} 
        label="Total Goals" 
        icon={Zap} 
        color="text-cyan-400" 
        bgColor="bg-cyan-500/10"
        glowColor="rgba(6, 182, 212, 0.15)"
        delay={400}
        index={2}
      />
    </div>
  );
}

export const AnimatedStatsCounter = memo(AnimatedStatsCounterComponent);
